import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listSites, type SiteRecord } from '@/api/sites';
import { useAuthStore } from '@/store/authStore';

/**
 * Multi-site: resolve WHICH site the current design page is editing, so every look/brand/page edit
 * targets an explicit site instead of silently drifting to the tenant's primary.
 *
 * `activeSiteId` (→ the `X-Site-Id` header) is the source of truth. This hook resolves it to a real
 * site record (falling back to the primary / first site), exposes the tenant's site list, and — so a
 * mutation's `X-Site-Id` header can never be absent while sites exist — writes the resolved site back
 * into `activeSiteId` when it's null. Use `activeSite?.id` as the React Query key for site-scoped
 * reads so the cache is per-site and can't serve one site's data for another.
 */
export function useActiveSite(): {
  activeSiteId: string | null;
  activeSite: SiteRecord | undefined;
  sites: SiteRecord[];
  hasMultiple: boolean;
  isLoading: boolean;
  /** True once the request header names the site the page shows (or the account has no site).
   *  Gate site-scoped reads on it: before it, a read goes out with no X-Site-Id (the primary's
   *  answer) and then again for the adopted site — the Dashboard's doubled calls. */
  ready: boolean;
  setActiveSiteId: (id: string | null) => void;
} {
  const activeSiteId = useAuthStore((s) => s.activeSiteId);
  const setActiveSiteId = useAuthStore((s) => s.setActiveSiteId);

  const { data: sites = [], isLoading, dataUpdatedAt, refetch } = useQuery({
    queryKey: ['my-sites'],
    queryFn: listSites,
    staleTime: 60_000,
  });

  // Nothing active yet → the site the URL names (`?site=`) when it is one of ours, and only then
  // the primary. Falling straight to the primary let this hook adopt it a moment before
  // ActiveSiteSync applied `?site`, so a secondary site's first reads could go out as the primary.
  const urlSite = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('site');
  const activeSite = useMemo(
    () =>
      sites.find((s) => s.id === activeSiteId) ??
      (!activeSiteId && urlSite ? sites.find((s) => s.id === urlSite) : undefined) ??
      sites.find((s) => s.is_primary) ??
      sites[0],
    [sites, activeSiteId, urlSite],
  );

  // Keep the header in step with the site we're SHOWING: if nothing is active yet but we have a
  // resolved site, adopt it so a write can't fall back to primary while the UI shows another site.
  useEffect(() => {
    if (!activeSiteId && activeSite) setActiveSiteId(activeSite.id);
  }, [activeSiteId, activeSite, setActiveSiteId]);

  // A selected site that isn't this account's (left in the browser by another sign-in, or a stale
  // `?site=`): every other route quietly falls back to the primary, but BLT refuses a site that
  // isn't the account's own, so the panel said "BLT isn't available on this site". Read the list
  // once more first (a site made a moment ago may not be in it yet), then drop it for the one shown.
  const recheck = useRef<{ siteId: string; at: number } | null>(null);
  const notOurs = !isLoading && sites.length > 0 && !!activeSiteId && !sites.some((s) => s.id === activeSiteId);
  useEffect(() => {
    if (!notOurs || !activeSiteId) return;
    if (recheck.current?.siteId !== activeSiteId) {
      recheck.current = { siteId: activeSiteId, at: dataUpdatedAt };
      void refetch();
      return;
    }
    if (dataUpdatedAt > recheck.current.at) setActiveSiteId(activeSite?.id ?? null);
  }, [notOurs, dataUpdatedAt, activeSiteId, activeSite, refetch, setActiveSiteId]);

  return {
    activeSiteId,
    activeSite,
    sites,
    hasMultiple: sites.length > 1,
    isLoading,
    ready: Boolean(activeSiteId) || (!isLoading && sites.length === 0),
    setActiveSiteId,
  };
}
